import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import config from 'config';
import useCurrentUser from 'hooks/useCurrentUser';
import jwtDecode from 'jwt-decode';
import { useEffect, useRef, useState } from 'react';
import { useGetClientsQuery } from 'services/api/clients.api';
import type { Client } from 'services/types/client';
import { AUTOSRELIF_BUSINESS_UNIT_ID } from 'utils/constants';

const BRANCH_OFFICES = ['Vitacura', 'Las Condes', 'La Cisterna'];

// System Colors
const COLORS = {
  primary: '#021024', // Navy blue
  secondary: '#4a5568', // Lighter blue
  success: '#00D084', // Green for status
  background: '#E8E8E8', // Light gray background
  cardBg: '#FFFFFF', // White for cards
  text: '#333333',
  textLight: '#666666',
};

/**
 * Relif CRM Demo Page
 * Simulates CRM system interface to demonstrate the iframe integration
 */
const RelifCRMDemo = () => {
  const currentUser = useCurrentUser();
  // Fetch real clients from autosrelif business unit
  const { data: clientsData, isLoading: isLoadingClients } = useGetClientsQuery({
    paginationModel: { page: 0, pageSize: 10 },
    filterModel: { items: [] },
    sortModel: [{ field: 'lastMessageDate', sort: 'desc' }],
    businessUnitId: currentUser?.isAdmin ? AUTOSRELIF_BUSINESS_UNIT_ID : undefined,
  });

  const clients = clientsData?.clients || [];
  // Configuration state
  const [masterToken, setMasterToken] = useState('');
  const [showMasterToken, setShowMasterToken] = useState(false);
  const [userEmail, setUserEmail] = useState('teresa.olivares@autosrelif.cl');
  const [userName, setUserName] = useState('Teresa Olivares');
  const [userRole, setUserRole] = useState<'seller' | 'admin'>('admin');
  const [userBranchOffice, setUserBranchOffice] = useState('Vitacura');

  // Authorization state
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [userJWT, setUserJWT] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<Date | null>(null);
  const [userId, setUserId] = useState<number | null>(null);

  // Iframe state
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [searchPhone, setSearchPhone] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // UI state
  const [showConfiguration, setShowConfiguration] = useState(true);
  const [searchPhoneInput, setSearchPhoneInput] = useState('');

  /**
   * Authorize user with master token
   */
  const handleAuthorizeUser = async () => {
    if (!masterToken) {
      setAuthError('Por favor ingresa el Master Token');
      return;
    }

    if (!userEmail) {
      setAuthError('Por favor ingresa el email del usuario');
      return;
    }

    setIsAuthorizing(true);
    setAuthError(null);

    try {
      const response = await fetch(`${config.apiUrl}/autosrelif/authorize-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${masterToken}`,
        },
        body: JSON.stringify({
          email: userEmail,
          name: userName || undefined,
          role: userRole === 'admin' ? 'admin' : 'seller',
          branchOffice: userBranchOffice || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData.message || 'Error al autorizar usuario');
      }

      const data = (await response.json()) as { token: string; type: string };
      setUserJWT(data.token);

      // Extract expiry date from JWT (exp claim)
      const decoded = jwtDecode<{ sub: number; exp: number }>(data.token);
      console.log('decoded', decoded);

      // exp is in seconds, convert to milliseconds
      const expiry = new Date(decoded.exp * 1000);
      setTokenExpiry(expiry);

      setUserId(decoded.sub);

      console.log('[AutosRelifCrmDemo] Usuario autorizado exitosamente');
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Error desconocido');
      setUserJWT(null);
      setTokenExpiry(null);
    } finally {
      setIsAuthorizing(false);
    }
  };

  // Send postMessage to iframe when client is selected
  useEffect(() => {
    if ((!selectedClient && !searchPhone) || !userJWT || !iframeRef.current) {
      return () => {};
    }
    // Wait for iframe to load
    const timer = setTimeout(() => {
      const iframe = iframeRef.current;
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            eventType: 'RELIF_USER_TOKEN',
            token: userJWT,
            name: userName,
            email: userEmail,
            role: userRole === 'admin' ? 'businessUnitAdmin' : 'seller',
            branchOffice: userBranchOffice,
          },
          window.location.origin
        );
        console.log('[AutosRelifCrmDemo] postMessage enviado al iframe');
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [selectedClient, searchPhone, userJWT, userName, userEmail, userRole, userBranchOffice]);

  const handleSelectClient = (client: Client) => {
    if (!userJWT) {
      setAuthError('Por favor autoriza un usuario primero');
      return;
    }
    setSelectedClient(client);
    setSearchPhone(null);
  };

  const handleSearchByPhone = (phone: string) => {
    if (!userJWT) {
      setAuthError('Por favor autoriza un usuario primero');
      return;
    }
    setSearchPhone(phone);
    setSelectedClient(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: COLORS.primary,
          color: 'white',
          px: 3,
          py: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1" sx={{ opacity: 0.9, fontSize: '1.2rem', fontWeight: 'bold' }}>
            CRM AUTOSRELIF
          </Typography>
        </Box>
        <Chip label="Modo Demo" sx={{ bgcolor: '#FFA726', color: 'white', fontWeight: 'bold' }} />
      </Box>
      {/* Main Content Area */}
      <Box sx={{ display: 'flex', height: '100%', bgcolor: COLORS.background, overflow: 'hidden' }}>
        {/* Main Content - Left Side */}
        <Box sx={{ flex: selectedClient || searchPhone ? '0 0 65%' : '1 1 100%', overflow: 'auto', p: 3 }}>
          {/* Configuration Panel */}
          <Card sx={{ mb: 3, boxShadow: 3, bgcolor: COLORS.cardBg }}>
            <Box
              sx={{
                bgcolor: COLORS.primary,
                color: 'white',
                px: 2,
                py: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() => setShowConfiguration(!showConfiguration)}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                ⚙️ Configuración de Demo
              </Typography>
              <IconButton sx={{ color: 'white' }}>
                {showConfiguration ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <CardContent>
              {showConfiguration && (
                <>
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    Esta es una demo de integración, en producción este token no será necesario.
                  </Alert>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    {/* Master Token */}
                    <TextField
                      fullWidth
                      label="Master Token (AUTOSRELIF_TOKEN)"
                      type={showMasterToken ? 'text' : 'password'}
                      value={masterToken}
                      onChange={(e) => setMasterToken(e.target.value)}
                      placeholder="Ingresa el token de API de AutosRelif"
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowMasterToken(!showMasterToken)} edge="end">
                                {showMasterToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />

                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Datos del Usuario a Autorizar
                    </Typography>

                    {/* User Configuration */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Email del Usuario"
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                        placeholder="usuario@autosrelif.cl"
                      />

                      <TextField
                        fullWidth
                        label="Nombre del Usuario"
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        placeholder="Nombre completo"
                      />

                      <FormControl fullWidth>
                        <InputLabel>Rol</InputLabel>
                        <Select
                          value={userRole}
                          label="Rol"
                          onChange={(e) => setUserRole(e.target.value as 'seller' | 'admin')}
                        >
                          <MenuItem value="seller">Vendedor (ver solo sus clientes)</MenuItem>
                          <MenuItem value="admin">Administrador (ver todos los clientes)</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth>
                        <InputLabel>Sucursal</InputLabel>
                        <Select
                          value={userBranchOffice}
                          label="Sucursal"
                          onChange={(e) => setUserBranchOffice(e.target.value)}
                        >
                          {BRANCH_OFFICES.map((office) => (
                            <MenuItem key={office} value={office}>
                              {office}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Authorize Button */}
                    <Button
                      variant="contained"
                      onClick={() => {
                        handleAuthorizeUser().catch(console.error);
                      }}
                      disabled={isAuthorizing || !masterToken || !userEmail}
                      sx={{
                        alignSelf: 'flex-start',
                        bgcolor: COLORS.primary,
                        '&:hover': { bgcolor: COLORS.secondary },
                        fontWeight: 'bold',
                        px: 4,
                        py: 1.5,
                      }}
                    >
                      {isAuthorizing ? 'Autorizando...' : 'Autorizar Usuario'}
                    </Button>

                    {/* Auth Error */}
                    {authError && (
                      <Alert severity="error" onClose={() => setAuthError(null)}>
                        {authError}
                      </Alert>
                    )}

                    {/* Success State */}
                    {userJWT && tokenExpiry && (
                      <Alert severity="success">
                        ✅ Usuario autorizado exitosamente
                        <br />
                        Token válido hasta: {tokenExpiry.toLocaleString('es-CL')}
                        <br />
                        ID de Usuario: {userId}
                        <br />
                        Token de Autenticación: {userJWT.substring(0, 50)}...
                      </Alert>
                    )}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>

          {/* Status Banner */}
          {userJWT && (
            <Box
              sx={{
                bgcolor: COLORS.success,
                color: 'white',
                px: 3,
                py: 1.5,
                mb: 3,
                borderRadius: 1,
                boxShadow: 2,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                📍 ESTADO: CONECTADO
              </Typography>
            </Box>
          )}

          {/* Search by Phone */}
          {userJWT && (
            <Card sx={{ mb: 3, boxShadow: 3, bgcolor: COLORS.cardBg }}>
              <Box sx={{ bgcolor: COLORS.primary, color: 'white', px: 3, py: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  🔍 Buscar Cliente por Teléfono
                </Typography>
              </Box>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={searchPhoneInput}
                    onChange={(e) => setSearchPhoneInput(e.target.value)}
                    placeholder="Ej: 56912345678"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && searchPhoneInput.trim()) {
                        handleSearchByPhone(searchPhoneInput.trim());
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (searchPhoneInput.trim()) {
                        handleSearchByPhone(searchPhoneInput.trim());
                      }
                    }}
                    disabled={!searchPhoneInput.trim()}
                    sx={{
                      bgcolor: COLORS.primary,
                      '&:hover': { bgcolor: COLORS.secondary },
                      fontWeight: 'bold',
                      px: 4,
                      py: 1.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Buscar
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Clients Table */}
          <Card sx={{ mb: 3, boxShadow: 3, bgcolor: COLORS.cardBg }}>
            <Box sx={{ bgcolor: COLORS.primary, color: 'white', px: 3, py: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                Clientes
              </Typography>
            </Box>
            <CardContent>
              <Typography variant="body2" sx={{ mb: 2, color: COLORS.textLight }}>
                Vendedor: {userName} ({userEmail})
              </Typography>

              {isLoadingClients && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 4,
                  }}
                >
                  <CircularProgress />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      ml: 2,
                    }}
                  >
                    Cargando clientes...
                  </Typography>
                </Box>
              )}

              {!isLoadingClients && clients.length === 0 && (
                <Alert severity="info">No hay clientes disponibles para mostrar en la demo.</Alert>
              )}

              {!isLoadingClients && clients.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ border: `1px solid ${COLORS.primary}` }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: COLORS.primary }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>RUT</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>NOMBRE</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>VEHÍCULO</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>TELÉFONO</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SUCURSAL</TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                          ACCIÓN
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clients.map((client) => (
                        <TableRow
                          key={client.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            bgcolor: selectedClient?.id === client.id ? `${COLORS.success}22` : 'inherit',
                            '&:hover': {
                              bgcolor: selectedClient?.id === client.id ? `${COLORS.success}33` : '#f5f5f5',
                            },
                          }}
                        >
                          <TableCell>{client.rut || '-'}</TableCell>
                          <TableCell sx={{ fontWeight: 'medium' }}>
                            {client.firstName} {client.lastName || ''}
                          </TableCell>
                          <TableCell>
                            {client.brandOfInterest}{' '}
                            {client.modelsOfInterest && client.modelsOfInterest.length > 0
                              ? client.modelsOfInterest[0]
                              : '-'}
                          </TableCell>
                          <TableCell>{client.phone}</TableCell>
                          <TableCell>{client.branchOffice || '-'}</TableCell>
                          <TableCell align="center">
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleSelectClient(client)}
                              disabled={!userJWT}
                              sx={{
                                bgcolor: COLORS.primary,
                                '&:hover': { bgcolor: COLORS.secondary },
                                fontWeight: 'bold',
                                textTransform: 'none',
                              }}
                            >
                              🔍 Ver en Relif
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Iframe Container - Right Side */}
        {(selectedClient || searchPhone) && (
          <Box
            sx={{
              flex: '0 0 35%',
              borderLeft: `3px solid ${COLORS.primary}`,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'white',
              mt: 1,
            }}
          >
            {/* Iframe Header */}
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: COLORS.primary,
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
                  {selectedClient ? 'Prospecto' : 'Buscar Cliente'}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  {selectedClient
                    ? `${selectedClient.firstName} ${selectedClient.lastName || ''}`
                    : `Teléfono: ${searchPhone}`}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setSelectedClient(null);
                  setSearchPhone(null);
                }}
                sx={{
                  bgcolor: 'white',
                  color: COLORS.primary,
                  '&:hover': { bgcolor: '#f0f0f0' },
                  fontWeight: 'bold',
                }}
              >
                Cerrar
              </Button>
            </Box>

            {/* Iframe */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <iframe
                ref={iframeRef}
                src={`/autosrelif-client?phone=${selectedClient?.phone || searchPhone}`}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: 'block',
                }}
                title={
                  selectedClient
                    ? `Cliente ${selectedClient.firstName} ${selectedClient.lastName || ''}`
                    : `Buscar cliente ${searchPhone}`
                }
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RelifCRMDemo;
